// Lehigh University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Lehigh's Common Data Set 2025-2026 (682026_CDS-2025-26_Revised_Published.pdf),
//    linked from the Common Data Set page on Lehigh's data.lehigh.edu site: C1 applicants, admits and enrollees for Fall 2025
//    with the residency table; C8 test policy for Fall 2027; C21/C22 early
//    plans with the early decision counts. Lehigh ticked the G0 box saying
//    its 2026-2027 costs were not yet final, so the costs come from the
//    Office of Financial Aid instead (its G1 figures match that page).
//  - Lehigh Admissions: How to Apply (deadlines, decision dates, test-optional
//    policy, FAQs) and Admissions Requirements (Early Decision Agreement,
//    the 7-year BA/DMD supplement). The deadline pages carry no year; they
//    are the pages as posted for the application that opened in August 2026,
//    beside the Class of 2029 profile.
//  - Lehigh Office of Financial Aid, Undergraduate: "2026-2027 Tuition &
//    Costs" (tuition, typical first-year housing, full meal plan, three
//    required fees, total $89,820), plus the $1,000 for books and $1,500 for
//    personal expenses the same page says Lehigh adds to the cost of
//    attendance. Lehigh does not count transportation.
//  - College Scorecard, school id 213543 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Lehigh University Common Data Set 2025-2026',
        url: 'https://data.lehigh.edu/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'Lehigh Admissions, How to Apply to Lehigh University',
        url: 'https://www2.lehigh.edu/admissions/apply',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Lehigh Admissions, Admissions Requirements',
        url: 'https://www2.lehigh.edu/admissions/admissions-requirements',
        read: '2026-10-01',
    },
    costs: {
        label: 'Lehigh Office of Financial Aid, Undergraduate: 2026-2027 Tuition and Costs',
        url: 'https://www2.lehigh.edu/financial-aid/undergraduate',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?213543-Lehigh-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'lehigh-university',
    kbId: 'lehigh_university',
    name: 'Lehigh University',
    shortName: 'Lehigh',
    location: 'Bethlehem, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Lehigh style guide); decoration only.
    branding: {
        primary: '#502D0E',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/lehigh_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 19342,
        admitted: 5601,
        enrolled: 1535,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 3090, admitted: 1010 },
            { group: 'Out-of-state', applied: 12070, admitted: 4415 },
            { group: 'International', applied: 4182, admitted: 176 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Lehigh\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II together', applied: 2040, admitted: 966 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them, with both early decision rounds counted together. They are part of the totals above. Lehigh has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'One combined-degree program has its own supplement and deadline:',
        rows: [
            { school: '7-year BA/DMD Bio-Dental program', requirement: 'Part of Penn\'s Accelerated Dental program. The Penn Supplement is required, due January 1, and you choose the 7-Year Combined-Degree Program in Dentistry on the Common Application. It is not an early decision commitment, and you cannot apply early decision at the same time. If not accepted into the program, you may ask to be considered for another college. Decisions come in early April.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'Mid February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March', binding: false },
        ],
        note: 'You apply through the Common Application or the Coalition Application with Scoir, with a $75 fee. Lehigh considers early decision I and II applicants for admission and merit scholarships in the same way. It has no early action plan.',
        source: 'apply',
        binding: 'Both early decision rounds are binding: you sign the Early Decision Agreement Form and, if admitted, withdraw all other applications and enroll.',
        bindingSource: 'requirements',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Lehigh is test-optional and has extended the policy indefinitely: you choose whether to send SAT or ACT scores. If you send scores from several test dates, Lehigh superscores them, and it has no preference between the two tests. You may self-report scores, but if you enroll, official scores must arrive before your first day of classes.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69420 },
            { item: 'Required fees (technology, activity, wellness)', amount: 1170 },
            { item: 'Housing (typical first-year)', amount: 11470 },
            { item: 'Food (full meal plan)', amount: 7760 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Personal expenses', amount: 1500 },
        ],
        total: 92320,
        note: 'For a first-year student living on campus. Lehigh\'s own total of $89,820 covers tuition, housing, food and fees; it adds $1,000 for books and $1,500 for personal expenses when it sets the cost of attendance for aid. Students in some programs and majors pay an added $870 fee. Lehigh does not include transportation.',
        source: 'costs',
        netPrice: {
            average: 25272,
            byIncome: [
                { income: '$0 to $30,000', amount: 14290 },
                { income: '$30,001 to $48,000', amount: 13708 },
                { income: '$48,001 to $75,000', amount: 19587 },
                { income: '$75,001 to $110,000', amount: 20869 },
                { income: 'Over $110,000', amount: 36233 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Lehigh\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
